import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface Props {
  children: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  /** Optional illustration override; defaults to the citation-card fan. */
  illustration?: ReactNode;
}

/**
 * Knowlez auth layout.
 *
 * Visual concept: a fan of citation cards on warm paper, stamped with an
 * indigo hanko. Echoes the in-product chunk-citation chips and the JP
 * archive aesthetic, with subtle horizontal hairlines reading as an old
 * card-catalog cabinet.
 *
 * Layout:
 *   - LEFT (form column): warm-paper background, brand chip top-left,
 *     letterpress submit. The form is the protagonist.
 *   - RIGHT (illustration): the citation-card fan — three layered
 *     "knowledge cards" with citation chips, plus a single indigo
 *     hanko stamp. Collapses on mobile.
 */
export function AuthLayout({ children, title, subtitle, footer, illustration }: Props) {
  const { t } = useTranslation();
  return (
    <div className="relative isolate min-h-screen paper-archive overflow-hidden">
      {/* Faint spotlight tint from above */}
      <div aria-hidden className="pointer-events-none absolute inset-0 paper-spotlight opacity-70" />

      {/* Brand chip — top-left, discreet way home */}
      <Link
        to="/"
        className="absolute top-5 left-5 z-10 inline-flex items-center gap-2 rounded-lg bg-white/85 backdrop-blur-sm px-3 py-1.5 hairline border shadow-card hover:shadow-card-lift transition"
      >
        <BrandMark />
        <span className="display text-[15px] font-semibold tracking-tight">
          {t('brand.name')}
        </span>
      </Link>

      <div className="relative grid min-h-screen lg:grid-cols-2">
        {/* Form column */}
        <div className="flex items-center justify-center px-5 py-20 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 0.84, 0.44, 1] }}
            className="w-full max-w-md"
          >
            <h1 className="display serif-jp text-3xl md:text-[2.4rem] font-semibold tracking-tight leading-[1.08] text-balance text-ink-900">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-[14.5px] text-ink-600 leading-relaxed text-pretty">
                {subtitle}
              </p>
            )}
            <div className="mt-8">{children}</div>
            {footer && (
              <div className="mt-7 pt-6 border-t border-ink-100 text-[13px] text-ink-600">
                {footer}
              </div>
            )}
          </motion.div>
        </div>

        {/* Illustration column */}
        <aside className="hidden lg:flex relative items-center justify-center px-8 py-12 border-l border-ink-100">
          {illustration ?? <CitationCardFan />}
        </aside>
      </div>

      {/* Bottom-left tiny meta — a single line of copyright that doesn't
          compete with the form for attention. */}
      <div className="absolute bottom-4 left-5 text-[10px] uppercase tracking-[0.18em] text-ink-400 font-mono">
        © {new Date().getFullYear()} {t('brand.name')}
      </div>
    </div>
  );
}

function BrandMark() {
  return <img src="/logo.png" alt="" aria-hidden className="h-8 w-8 rounded-md object-contain shrink-0" />;
}

/**
 * The signature illustration: three overlapping "knowledge cards"
 * arranged in a soft fan, each carrying:
 *   - a small heading (mock document title in Noto Serif JP)
 *   - one or two citation chips ([chunk:NNN]) in orange
 *   - a thin horizontal hairline rule
 * Behind the cards, a single indigo hanko stamp anchors the composition.
 */
function CitationCardFan() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full max-w-md aspect-[3/4]">
      {/* Hanko stamp — behind the cards, slightly off-center */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.6, ease: [0.16, 0.84, 0.44, 1] }}
        className="absolute top-6 right-6 hanko h-20 w-20 text-2xl rotate-[6deg]"
        aria-hidden
      >
        <span className="font-serif">知</span>
      </motion.div>

      {/* Card 1 — back, rotated left */}
      <FanCard
        delay={0.35}
        rotate={-7}
        translate={{ x: -28, y: 60 }}
        zIndex={1}
        title={t('auth.illus.card1_title', { defaultValue: 'Q3 議事録 — Project Atlas' })}
        snippet={t('auth.illus.card1_body', {
          defaultValue: 'ベンダーA社のAPI遅延について議論。次四半期までに代替評価。',
        })}
        chips={['1242', '1287']}
      />

      {/* Card 2 — middle, slight tilt */}
      <FanCard
        delay={0.5}
        rotate={1}
        translate={{ x: 0, y: 30 }}
        zIndex={2}
        title={t('auth.illus.card2_title', { defaultValue: 'FY2026 Q1 Risk Brief' })}
        snippet={t('auth.illus.card2_body', {
          defaultValue:
            'Access-revocation SLA averages 4.2d against 1d policy. Recommend escalation.',
        })}
        chips={['2118', '2101']}
      />

      {/* Card 3 — front, rotated right */}
      <FanCard
        delay={0.65}
        rotate={6}
        translate={{ x: 26, y: 0 }}
        zIndex={3}
        title={t('auth.illus.card3_title', { defaultValue: '稟議書 #487 — Vendor expansion' })}
        snippet={t('auth.illus.card3_body', {
          defaultValue: 'CFO sign-off pending. Hanko applied 2026-05-12.',
        })}
        chips={['3041']}
      />

      {/* Tagline below */}
      <div className="absolute -bottom-4 left-0 right-0 text-center">
        <p className="display text-[13px] text-ink-500 tracking-tight italic">
          {t('auth.illus.tagline', {
            defaultValue: '森羅万象を、ひとつの知に。 — Every thing, made present.',
          })}
        </p>
      </div>
    </div>
  );
}

function FanCard({
  delay,
  rotate,
  translate,
  zIndex,
  title,
  snippet,
  chips,
}: {
  delay: number;
  rotate: number;
  translate: { x: number; y: number };
  zIndex: number;
  title: string;
  snippet: string;
  chips: string[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: translate.y + 20, x: translate.x, rotate }}
      animate={{ opacity: 1, y: translate.y, x: translate.x, rotate }}
      transition={{ delay, duration: 0.7, ease: [0.16, 0.84, 0.44, 1] }}
      style={{ zIndex }}
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[78%] card-paper p-5 font-sans"
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-400">
          chunk
        </p>
        <p className="font-mono text-[9.5px] text-ink-400">2026-05</p>
      </div>
      <h4 className="display serif-jp mt-2 text-[15px] font-semibold leading-snug text-ink-900">
        {title}
      </h4>
      <hr className="my-3 border-ink-100" />
      <p className="text-[12px] leading-relaxed text-ink-700">{snippet}</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span key={c} className="chip-citation">
            chunk:{c}
          </span>
        ))}
      </div>
    </motion.div>
  );
}
